import type { TableView } from "./export.types";

/** A4 deitado: a listagem tem mais colunas do que cabe em pé. */
const PAGE_WIDTH = 842;
const PAGE_HEIGHT = 595;
const MARGIN = 32;

const TITLE_FONT_SIZE = 14;
const META_FONT_SIZE = 9;
const TABLE_FONT_SIZE = 9;

const ROW_HEIGHT = 16;
const HEADER_HEIGHT = 18;
const CELL_PADDING = 4;

/** Largura média do glifo do Helvetica, usada para truncar o texto da célula. */
const CHAR_WIDTH_RATIO = 0.5;

const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const TABLE_TOP = PAGE_HEIGHT - MARGIN - 46;
const TABLE_BOTTOM = MARGIN + 16;

const MIN_COLUMN_WEIGHT = 6;
const MAX_COLUMN_WEIGHT = 40;
const WIDTH_SAMPLE_ROWS = 50;

const TITLE_COLOR = "0 0.259 0.361";
const META_COLOR = "0.267 0.482 0.588";
const HEADER_FILL = "0.949 0.973 0.976";
const LINE_COLOR = "0.749 0.843 0.886";
const TEXT_COLOR = "0.208 0.329 0.412";

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

/** Glifos do WinAnsiEncoding fora da faixa Latin-1 direta. */
const WIN_ANSI_EXTRAS: Record<string, number> = {
  "€": 0x80,
  "‚": 0x82,
  ƒ: 0x83,
  "„": 0x84,
  "…": 0x85,
  "†": 0x86,
  "‡": 0x87,
  "‰": 0x89,
  Š: 0x8a,
  "‹": 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97,
  "™": 0x99,
  š: 0x9a,
  "›": 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

/**
 * O texto vai para o PDF em escapes octais WinAnsi, o que mantém o arquivo
 * inteiro em ASCII — os deslocamentos da tabela `xref` são então as próprias
 * posições dos caracteres.
 */
export function toPdfText(value: string): string {
  let escaped = "";

  for (const char of value.replace(/→/g, "->")) {
    const code = char.codePointAt(0) ?? 0;

    if (char === "(" || char === ")" || char === "\\") {
      escaped += `\\${char}`;
      continue;
    }

    if (code >= 32 && code <= 126) {
      escaped += char;
      continue;
    }

    const byte = code >= 0xa0 && code <= 0xff ? code : WIN_ANSI_EXTRAS[char];
    escaped += byte === undefined ? "?" : `\\${byte.toString(8).padStart(3, "0")}`;
  }

  return escaped;
}

function round(value: number): string {
  return value.toFixed(2);
}

export function fitToWidth(value: string, width: number, fontSize: number): string {
  const text = value.replace(/\s+/g, " ").trim();
  const limit = Math.max(1, Math.floor((width - CELL_PADDING * 2) / (fontSize * CHAR_WIDTH_RATIO)));

  return text.length <= limit ? text : `${text.slice(0, Math.max(1, limit - 3))}...`;
}

/** Colunas com conteúdo mais longo recebem uma fatia maior da largura útil. */
export function measureColumns(view: TableView): number[] {
  const samples = view.rows.slice(0, WIDTH_SAMPLE_ROWS);

  const weights = view.columns.map((column, index) => {
    const longest = samples.reduce(
      (largest, row) => Math.max(largest, (row[index] ?? "").length),
      column.length,
    );

    return Math.min(Math.max(longest, MIN_COLUMN_WEIGHT), MAX_COLUMN_WEIGHT);
  });

  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;

  return weights.map((weight) => (weight / total) * CONTENT_WIDTH);
}

function drawText(text: string, x: number, y: number, font: string, size: number, color: string) {
  return `BT /${font} ${size} Tf ${color} rg 1 0 0 1 ${round(x)} ${round(y)} Tm (${toPdfText(text)}) Tj ET`;
}

function drawLine(y: number): string {
  return `${LINE_COLOR} RG 0.5 w ${round(MARGIN)} ${round(y)} m ${round(MARGIN + CONTENT_WIDTH)} ${round(y)} l S`;
}

function drawRow(
  cells: string[],
  widths: number[],
  baseline: number,
  font: string,
  color: string,
): string[] {
  const operations: string[] = [];
  let x = MARGIN;

  widths.forEach((width, index) => {
    operations.push(
      drawText(
        fitToWidth(cells[index] ?? "", width, TABLE_FONT_SIZE),
        x + CELL_PADDING,
        baseline,
        font,
        TABLE_FONT_SIZE,
        color,
      ),
    );
    x += width;
  });

  return operations;
}

function buildPageContent(
  view: TableView,
  widths: number[],
  rows: string[][],
  pageNumber: number,
  pageCount: number,
): string {
  const operations: string[] = [
    drawText(view.title, MARGIN, PAGE_HEIGHT - MARGIN - 12, "F2", TITLE_FONT_SIZE, TITLE_COLOR),
    drawText(
      `Gerado em ${DATE_TIME_FORMATTER.format(new Date())} — ${view.rows.length} ${
        view.rows.length === 1 ? "registro" : "registros"
      }`,
      MARGIN,
      PAGE_HEIGHT - MARGIN - 28,
      "F1",
      META_FONT_SIZE,
      META_COLOR,
    ),
    `${HEADER_FILL} rg ${round(MARGIN)} ${round(TABLE_TOP - HEADER_HEIGHT)} ${round(CONTENT_WIDTH)} ${round(HEADER_HEIGHT)} re f`,
  ];

  operations.push(
    ...drawRow(view.columns, widths, TABLE_TOP - HEADER_HEIGHT + 6, "F2", TITLE_COLOR),
    drawLine(TABLE_TOP - HEADER_HEIGHT),
  );

  rows.forEach((row, index) => {
    const baseline = TABLE_TOP - HEADER_HEIGHT - ROW_HEIGHT * (index + 1) + 5;

    operations.push(...drawRow(row, widths, baseline, "F1", TEXT_COLOR), drawLine(baseline - 5));
  });

  operations.push(
    drawText(
      `Página ${pageNumber} de ${pageCount}`,
      MARGIN,
      TABLE_BOTTOM - 10,
      "F1",
      META_FONT_SIZE,
      META_COLOR,
    ),
  );

  return operations.join("\n");
}

export function rowsPerPage(): number {
  return Math.max(1, Math.floor((TABLE_TOP - HEADER_HEIGHT - TABLE_BOTTOM) / ROW_HEIGHT));
}

function paginate(rows: string[][]): string[][][] {
  const size = rowsPerPage();
  const pages: string[][][] = [];

  for (let index = 0; index < rows.length; index += size) {
    pages.push(rows.slice(index, index + size));
  }

  return pages.length > 0 ? pages : [[]];
}

function toBytes(document: string): Uint8Array {
  const bytes = new Uint8Array(document.length);

  for (let index = 0; index < document.length; index += 1) {
    bytes[index] = document.charCodeAt(index) & 0xff;
  }

  return bytes;
}

/**
 * Documento PDF 1.4 escrito à mão: catálogo, páginas, as duas fontes padrão e um
 * fluxo de conteúdo por página, seguidos da tabela `xref` com o deslocamento de
 * cada objeto.
 */
export function buildPdfFile(view: TableView): Uint8Array {
  const widths = measureColumns(view);
  const pages = paginate(view.rows);

  const firstPageObject = 5;
  const toPageId = (index: number) => firstPageObject + index * 2;
  const pageIds = pages.map((_, index) => toPageId(index));

  const objects: string[] = [
    `<< /Type /Catalog /Pages 2 0 R >>`,
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>`,
  ];

  pages.forEach((rows, index) => {
    const contentId = toPageId(index) + 1;
    const content = buildPageContent(view, widths, rows, index + 1, pages.length);

    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`,
      `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    );
  });

  let document = "%PDF-1.4\n";
  const offsets: number[] = [];

  objects.forEach((body, index) => {
    offsets.push(document.length);
    document += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });

  const xrefOffset = document.length;

  document += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  document += offsets.map((offset) => `${String(offset).padStart(10, "0")} 00000 n \n`).join("");
  document += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  return toBytes(document);
}
